/**
 * Presentation helpers for usage analytics.
 *
 * Status strings come from the API as UsageStatus names ("Ok" | "Warn" | "Over" |
 * "NoTier") rather than enum indexes, so these lookups are stable if the backend enum
 * is ever reordered.
 */

import { formatLargeNumber, formatNumber } from "./formatUtils";

export const USAGE_STATUS = {
  OK: "Ok",
  WARN: "Warn",
  OVER: "Over",
  NO_TIER: "NoTier",
};

export const USAGE_TREND = {
  UP: "Up",
  DOWN: "Down",
  FLAT: "Flat",
};

/** Month-window options for the range selector. */
export const MONTH_RANGE_OPTIONS = [
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
  { value: 24, label: "Last 24 months" },
];

const STATUS_VARIANTS = {
  [USAGE_STATUS.OK]: "success",
  [USAGE_STATUS.WARN]: "warning",
  [USAGE_STATUS.OVER]: "danger",
  [USAGE_STATUS.NO_TIER]: "secondary",
};

const STATUS_LABELS = {
  [USAGE_STATUS.OK]: "Within tier",
  [USAGE_STATUS.WARN]: "Approaching limit",
  [USAGE_STATUS.OVER]: "Over limit",
  [USAGE_STATUS.NO_TIER]: "No tier assigned",
};

/**
 * Bootstrap variant for a status. Unknown values fall back to "secondary" so an
 * unexpected string renders neutrally rather than as a false "all good".
 */
export const getStatusVariant = (status) => STATUS_VARIANTS[status] || "secondary";

export const getStatusLabel = (status) => STATUS_LABELS[status] || "Unknown";

/**
 * Trend arrow and colour.
 *
 * Rising usage is NOT good news here — it means a client is heading for their limit —
 * so "Up" is deliberately warning-coloured rather than success-coloured.
 */
export const getTrendDisplay = (trend) => {
  switch (trend) {
    case USAGE_TREND.UP:
      return { symbol: "▲", variant: "warning", label: "Trending up" };
    case USAGE_TREND.DOWN:
      return { symbol: "▼", variant: "info", label: "Trending down" };
    default:
      return { symbol: "–", variant: "secondary", label: "Flat" };
  }
};

/** Formats usage units compactly (e.g. "1.6M"). */
export const formatUnits = (units) => formatLargeNumber(units ?? 0);

/** Formats usage units in full (e.g. "1,587,178") for tooltips and tables. */
export const formatUnitsExact = (units) => formatNumber(units ?? 0);

/**
 * Percent of limit for display. Null (no tier) renders as an em dash rather than
 * "0%", which would wrongly imply the client is comfortably inside a limit.
 */
export const formatPercentOfLimit = (percent) =>
  percent === null || percent === undefined ? "—" : `${Number(percent).toFixed(1)}%`;

/**
 * Signed percentage change. Null means there was no baseline to compare against
 * (growth from zero is not a percentage), so it shows a dash.
 */
export const formatChange = (percent) => {
  if (percent === null || percent === undefined) return "—";

  const value = Number(percent);
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
};

/** Bootstrap text colour for a change value, using the same "up is concerning" logic. */
export const getChangeVariant = (percent) => {
  if (percent === null || percent === undefined) return "text-muted";
  if (Number(percent) > 0) return "text-warning";
  if (Number(percent) < 0) return "text-info";
  return "text-muted";
};

/**
 * Caps the progress bar at 100 so an over-limit client does not overflow its track.
 * The real percentage is still shown as the label.
 */
export const progressValue = (percent) => Math.min(Math.max(Number(percent) || 0, 0), 100);

/**
 * Label for a chart point, marking the in-progress month.
 *
 * The current month is always partial — the refresh proc rewrites a whole month at a
 * time — so it must never be presented as comparable to a completed month.
 */
export const pointLabel = (point) => {
  if (!point) return "";
  if (point.isForecast) return `${point.label} (forecast)`;
  if (point.isPartial) return `${point.label} (in progress)`;
  return point.label;
};

/** True when the client has a tier and therefore a limit to measure against. */
export const hasTier = (usage) => Boolean(usage?.hasTier && usage?.effectiveLimit > 0);

/** Labels of the completed months in a series, oldest first. Excludes the partial one. */
export const completeMonthLabels = (series = []) =>
  series.filter((point) => !point.isPartial && !point.isForecast).map((point) => point.label);

/**
 * Which two months a month-over-month figure actually compares, e.g. "Jun 2026 → Jul 2026".
 *
 * Shown under the MoM and Trend tiles because those numbers are computed from the last two
 * COMPLETE months — not the month you are currently looking at. Without this a reader sees
 * "Down 61%" while the chart shows a spike and has no way to reconcile the two.
 */
export const monthOverMonthPeriod = (series = []) => {
  const labels = completeMonthLabels(series);
  if (labels.length < 2) return null;

  return `${labels[labels.length - 2]} → ${labels[labels.length - 1]}`;
};

/** The most recent completed month's label, or null when there is none. */
export const lastCompleteMonthLabel = (series = []) => {
  const labels = completeMonthLabels(series);
  return labels.length > 0 ? labels[labels.length - 1] : null;
};

/**
 * Confidence badge for a forecast. Low confidence is shown rather than hidden so a
 * shaky projection is not mistaken for a reliable one.
 */
export const getConfidenceVariant = (confidence) => {
  switch (confidence) {
    case "High":
      return "success";
    case "Medium":
      return "warning";
    default:
      return "secondary";
  }
};

/**
 * Merges observed and forecast points into one series for a combined chart, keeping
 * actual and projected values on separate keys so they can be drawn with different
 * strokes and never joined into a single misleading line.
 *
 * @param {Array}  series             observed months, oldest first
 * @param {Array}  forecast           projected months, oldest first
 * @param {number} [projectedCurrent] month-end projection for the in-progress month,
 *                                    used to anchor the forecast line
 */
export const buildChartSeries = (series = [], forecast = [], projectedCurrent = null) => {
  const actual = series.map((point) => ({
    ...point,
    name: point.label,
    actualUnits: point.usageUnits,
    forecastUnits: null,
  }));

  const projected = forecast.map((point) => ({
    ...point,
    name: point.label,
    actualUnits: null,
    forecastUnits: point.usageUnits,
  }));

  // Bridge the gap so the dashed forecast line connects to the series instead of
  // floating detached.
  //
  // The anchor deliberately AVOIDS the partial current month's actual value. On the 3rd
  // of a month a client with a 150k/month run rate has ~5k banked, so anchoring there
  // draws a cliff down to 5k and then a spike back up — exactly the misreading the
  // partial-month shading exists to prevent. Anchor on the month-end projection when we
  // have one, otherwise on the last COMPLETE month.
  if (actual.length > 0 && projected.length > 0) {
    const lastIndex = actual.length - 1;
    const last = actual[lastIndex];

    if (!last.isPartial) {
      actual[lastIndex] = { ...last, forecastUnits: last.actualUnits };
    } else if (projectedCurrent !== null && projectedCurrent !== undefined) {
      actual[lastIndex] = { ...last, forecastUnits: projectedCurrent };
    } else if (lastIndex > 0) {
      actual[lastIndex - 1] = {
        ...actual[lastIndex - 1],
        forecastUnits: actual[lastIndex - 1].actualUnits,
      };
    }
  }

  return [...actual, ...projected];
};
